import React, { useState, useEffect } from 'react';
import EmptyState from '../../../components/ui/EmptyState';
import { ListSkeleton } from '../../../components/ui/Skeleton';
import Icon from '../../../components/AppIcon';
import { adminService } from '../../../services/adminService';
import { format } from 'date-fns';

const SystemActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getActivityLogs(50);
      setActivities(data);
    } catch (err) {
      setError(err?.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      user: 'UserPlus',
      conference: 'FolderPlus',
      submission: 'FileText'
    };
    return icons?.[type] || 'Activity';
  };

  const getActivityColor = (type) => {
    const colors = {
      user: 'text-primary',
      conference: 'text-secondary',
      submission: 'text-success'
    };
    return colors?.[type] || 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground mb-2">System Activity</h2>
        <p className="text-sm text-muted-foreground">Recent platform activities and events</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-error/10 border-2 border-error rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <ListSkeleton rows={6} />
      ) : activities?.length === 0 ? (
        <EmptyState
          icon="Activity"
          title="Nothing has happened yet"
          body="Sign-ins, submissions and conference changes across the platform show up here as they occur."
        />
      ) : (
        <div className="space-y-4">
          {activities?.map((activity, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 bg-muted rounded-lg hover:bg-muted/70 transition-colors"
            >
              <div className={`w-10 h-10 rounded-lg bg-background border-2 border-border flex items-center justify-center flex-shrink-0`}>
                <Icon
                  name={getActivityIcon(activity?.type)}
                  size={20}
                  className={getActivityColor(activity?.type)}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground font-medium">{activity?.description}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {formatTimestamp(activity?.timestamp)}
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className="px-2 py-1 text-xs font-medium bg-background border border-border">
                  {activity?.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SystemActivity;